<template>
  <q-dialog
    :visible="visible"
    title="文件层级转换"
    width="1180px"
    :innerScroll="false"
    :showFooter="false"
    @close="handleDialogClose"
  >
    <div
      v-loading="loading"
      class="conversion-dialog"
      element-loading-text="数据加载中"
      element-loading-spinner="el-icon-loading"
    >
      <div class="conversion-summary">
        <strong>旧切片文件夹转换</strong>
        <span>共 {{ task.totalItemCount || 0 }} 项，待处理 <b>{{ pendingCount }}</b> 项</span>
      </div>

      <div class="conversion-note">
        <i class="el-icon-info"></i>
        按文件管理器方式查看对应关系：左侧是已上传的旧目录，右侧是可替换的新目录。规则外项目优先置顶。
      </div>

      <div class="conversion-workspace">
        <aside class="directory-panel old-directory-panel">
          <div class="panel-heading">
            <strong>旧切片目录</strong>
            <span>{{ task.totalItemCount || 0 }} 项</span>
          </div>
          <div class="directory-list">
            <button
              v-for="item in oldItems"
              :key="item.itemId"
              type="button"
              class="directory-item old-directory-item"
              :class="{ selected: selectedItemId === item.itemId, 'out-of-rule': item.topFlag === 'Y' }"
              @click="selectItem(item.itemId)"
            >
              <i class="directory-icon" :class="item.objectType === 'FILE' ? 'el-icon-document' : 'el-icon-folder'"></i>
              <span class="directory-item-main">
                <strong>{{ item.sourceName }}</strong>
                <small>{{ item.sourcePath }}</small>
              </span>
              <em :class="statusClass(item)">{{ statusText(item) }}</em>
            </button>
            <div v-if="!oldItems.length && !loading" class="directory-empty">暂无待转换文件</div>
          </div>
        </aside>

        <section class="mapping-panel">
          <div class="panel-heading">
            <strong>当前对应关系</strong>
          </div>
          <div v-if="selectedItem" class="mapping-content">
            <div class="source-card">
              <i class="directory-icon" :class="selectedItem.objectType === 'FILE' ? 'el-icon-document' : 'el-icon-folder'"></i>
              <div class="source-info">
                <small>旧目录{{ selectedItem.objectType === 'FILE' ? '文件' : '文件夹' }}</small>
                <template v-if="editingName">
                  <el-input
                    ref="renameInput"
                    v-model="draftName"
                    size="mini"
                    maxlength="50"
                    @keyup.enter.native="saveName"
                  ></el-input>
                  <div class="rename-actions">
                    <el-button type="text" :loading="renaming" @click="saveName">保存</el-button>
                    <el-button type="text" :disabled="renaming" @click="cancelName">取消</el-button>
                  </div>
                </template>
                <template v-else>
                  <strong>{{ selectedItem.sourceName }}</strong>
                  <button
                    v-if="selectedItem.objectType === 'FOLDER'"
                    type="button"
                    class="detail-link"
                    @click="startRename"
                  >修改名称</button>
                </template>
                <em>{{ selectedItem.sourcePath }}</em>
              </div>
            </div>

            <div class="mapping-arrow">
              <i class="el-icon-bottom"></i>
            </div>

            <label class="target-select">
              <span>新目录位置</span>
              <el-select
                :value="selectedItem.targetPath"
                size="mini"
                :disabled="updatingTarget"
                placeholder="请选择新目录位置"
                @change="updateTarget"
              >
                <el-option
                  v-for="targetPath in newFolderSpec"
                  :key="targetPath"
                  :label="targetPath"
                  :value="targetPath"
                ></el-option>
              </el-select>
            </label>
            <p class="mapping-help" :class="statusClass(selectedItem)">
              {{ selectedItem.matchStatus === 'MATCHED' ? '已匹配，可重新选择位置' : '该项目尚未匹配，请选择一个新目录位置' }}
            </p>
          </div>
          <div v-else class="empty-state">暂无可转换内容</div>
        </section>

        <aside class="directory-panel new-directory-panel">
          <div class="panel-heading">
            <strong>新切片目录</strong>
            <span class="new-directory-actions">
              <button type="button" class="detail-link" :disabled="!taskId" @click="downloadPreview">文件下载</button>
              <button type="button" class="detail-link" :disabled="!taskId" @click="openPreview">文件预览</button>
              {{ newFolderSpec.length }} 个位置
            </span>
          </div>
          <div class="directory-list">
            <div
              v-for="targetPath in newFolderSpec"
              :key="targetPath"
              class="directory-item target-directory-item"
              :class="{ active: selectedItem && selectedItem.targetPath === targetPath }"
            >
              <i class="directory-icon el-icon-folder"></i>
              <span class="directory-item-main">
                <strong>{{ targetPath }}</strong>
                <small>{{ targetItems(targetPath).length ? targetItems(targetPath).join('、') : '暂无映射内容' }}</small>
              </span>
              <em v-if="targetItems(targetPath).length">{{ targetItems(targetPath).length }}项</em>
            </div>
            <div v-if="!newFolderSpec.length && !loading" class="directory-empty">暂无新目录规范</div>
          </div>
        </aside>
      </div>

      <div class="conversion-footer">
        <span>还有 {{ pendingCount }} 项待处理</span>
        <div>
          <el-button size="mini" :loading="cancelling" @click="cancelConversion">取消</el-button>
          <el-button size="mini" type="primary" :loading="confirming" :disabled="!canConfirm" @click="confirmConversion">确认上传</el-button>
        </div>
      </div>
    </div>

    <el-dialog
      title="新切片目录-文件预览"
      :visible.sync="previewVisible"
      width="520px"
      append-to-body
      class="preview-dialog"
    >
      <div v-loading="previewLoading" class="preview-content" element-loading-text="加载中">
        <div class="preview-toolbar">
          <el-button type="text" size="mini" :disabled="!previewHistory.length" @click="goPreviewParent">
            <i class="el-icon-back"></i> 返回上级
          </el-button>
          <span>{{ previewData.breadcrumbs && previewData.breadcrumbs.length ? previewData.breadcrumbs.join(' / ') : '根目录' }}</span>
        </div>
        <div class="preview-list">
          <button
            v-for="folder in previewData.folders"
            :key="folder.path"
            type="button"
            class="preview-entry"
            @click="openPreviewFolder(folder)"
          >
            <i class="el-icon-folder"></i>
            <span>{{ folder.name }}</span>
            <small>{{ folder.childCount }} 项</small>
          </button>
          <div v-for="file in previewData.files" :key="file.path" class="preview-entry preview-file-entry">
            <i class="el-icon-picture-outline"></i>
            <span>{{ file.name }}</span>
            <small>{{ formatFileSize(file.fileSize) }}</small>
          </div>
          <div v-if="!previewLoading && !previewData.folders.length && !previewData.files.length" class="preview-empty">暂无文件</div>
        </div>
      </div>
    </el-dialog>
  </q-dialog>
</template>

<script>
import qDialog from '@/components/qDialog/qDialog.vue'

export default {
  name: 'FileHierarchyConversionDialog',
  components: { qDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    spuId: {
      type: [String, Number],
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      taskId: '',
      task: {},
      oldItems: [],
      newFolderSpec: [],
      selectedItemId: null,
      editingName: false,
      draftName: '',
      updatingTarget: false,
      renaming: false,
      confirming: false,
      cancelling: false,
      skipCancel: false,
      cancelAfterScan: false,
      previewVisible: false,
      previewLoading: false,
      previewHistory: [],
      previewData: {
        breadcrumbs: [],
        currentPath: '',
        folders: [],
        files: []
      }
    }
  },
  computed: {
    selectedItem () {
      return this.oldItems.find(item => item.itemId === this.selectedItemId)
    },
    pendingCount () {
      return this.task.pendingItemCount || 0
    },
    canConfirm () {
      return this.pendingCount === 0 && !this.loading && !this.confirming
    }
  },
  watch: {
    visible (value) {
      if (value) this.initializeConversion()
    }
  },
  methods: {
    async initializeConversion () {
      if (this.loading) return
      if (!this.spuId) {
        this.$message.warning('缺少SPU信息，无法进行文件层级转换')
        return
      }

      this.skipCancel = false
      this.cancelAfterScan = false
      this.resetTaskData()
      this.loading = true
      try {
        const res = await this.$q.newProduct.sliceConvertScan({
          data: {
            spuId: this.spuId
          }
        })
        if (res.code == 200) {
          this.taskId = res.data.taskId
          if (this.cancelAfterScan || !this.visible) {
            await this.cancelConversion()
            return
          }
          await this.getTaskDetail()
        }
      } catch (error) {
        // 请求异常由全局拦截器提示
      } finally {
        this.loading = false
      }
    },
    resetTaskData () {
      this.taskId = ''
      this.task = {}
      this.oldItems = []
      this.newFolderSpec = []
      this.selectedItemId = null
      this.editingName = false
      this.draftName = ''
      this.previewVisible = false
      this.previewHistory = []
      this.previewData = {
        breadcrumbs: [],
        currentPath: '',
        folders: [],
        files: []
      }
    },
    async getTaskDetail (selectedItemId = this.selectedItemId) {
      const res = await this.$q.newProduct.sliceConvertDetail({
        data: {
          taskId: this.taskId
        }
      })
      if (res.code != 200) return

      this.task = res.data.task
      this.oldItems = res.data.task.items
      this.newFolderSpec = res.data.newFolderSpec
      const selectedItem = this.oldItems.find(item => item.itemId === selectedItemId)
      this.selectedItemId = selectedItem ? selectedItem.itemId : (this.oldItems[0] ? this.oldItems[0].itemId : null)
    },
    statusText (item) {
      if (item.matchStatus === 'MATCHED') return '已匹配'
      if (item.matchStatus === 'IGNORED') return '已忽略'
      return '待处理'
    },
    statusClass (item) {
      if (item.matchStatus === 'MATCHED') return 'is-matched'
      if (item.matchStatus === 'IGNORED') return 'is-ignored'
      return 'is-pending'
    },
    selectItem (itemId) {
      this.selectedItemId = itemId
      this.editingName = false
    },
    async updateTarget (targetPath) {
      const item = this.selectedItem
      if (!item || !targetPath || item.targetPath === targetPath || this.updatingTarget) return

      this.updatingTarget = true
      try {
        const res = await this.$q.newProduct.sliceConvertUpdateTarget({
          data: {
            taskId: this.taskId,
            itemId: item.itemId,
            targetPath
          }
        })
        if (res.code == 200) {
          await this.getTaskDetail(item.itemId)
          this.$message.success('新目录位置已更新')
        }
      } catch (error) {
        // 请求异常由全局拦截器提示
      } finally {
        this.updatingTarget = false
      }
    },
    startRename () {
      this.draftName = this.selectedItem.sourceName
      this.editingName = true
      this.$nextTick(() => {
        if (this.$refs.renameInput) this.$refs.renameInput.focus()
      })
    },
    async saveName () {
      const newName = this.draftName.trim()
      if (!newName) {
        this.$message.warning('文件夹名称不能为空')
        return
      }
      if (!this.selectedItem || newName === this.selectedItem.sourceName || this.renaming) {
        this.editingName = false
        return
      }

      this.renaming = true
      try {
        const res = await this.$q.newProduct.sliceConvertRenameFolder({
          data: {
            taskId: this.taskId,
            itemId: this.selectedItem.itemId,
            newName
          }
        })
        if (res.code == 200) {
          await this.getTaskDetail(this.selectedItemId)
          this.editingName = false
          this.$message.success('文件夹名称已更新')
        }
      } catch (error) {
        // 请求异常由全局拦截器提示
      } finally {
        this.renaming = false
      }
    },
    cancelName () {
      if (!this.renaming) this.editingName = false
    },
    targetItems (targetPath) {
      return this.oldItems
        .filter(item => item.matchStatus === 'MATCHED' && item.targetPath === targetPath)
        .map(item => item.sourceName)
    },
    async openPreview () {
      this.previewVisible = true
      this.previewHistory = []
      await this.loadPreview('')
    },
    async loadPreview (path) {
      this.previewLoading = true
      try {
        const res = await this.$q.newProduct.sliceConvertPreview({
          data: {
            taskId: this.taskId,
            path
          }
        })
        if (res.code == 200) this.previewData = res.data
      } catch (error) {
        // 请求异常由全局拦截器提示
      } finally {
        this.previewLoading = false
      }
    },
    async openPreviewFolder (folder) {
      this.previewHistory.push(this.previewData.currentPath)
      await this.loadPreview(folder.path)
    },
    async goPreviewParent () {
      if (!this.previewHistory.length) return
      await this.loadPreview(this.previewHistory.pop())
    },
    formatFileSize (fileSize) {
      if (!fileSize) return '-'
      if (fileSize < 1024) return `${fileSize} B`
      if (fileSize < 1024 * 1024) return `${(fileSize / 1024).toFixed(1)} KB`
      return `${(fileSize / 1024 / 1024).toFixed(1)} MB`
    },
    async downloadPreview () {
      try {
        const res = await this.$q.newProduct.sliceConvertDownload({
          data: {
            taskId: this.taskId,
            path: ''
          }
        })
        if (res.code == 200 && res.data) {
          const link = document.createElement('a')
          link.href = res.data
          link.download = ''
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          this.$message.success('文件下载已开始')
        }
      } catch (error) {
        // 请求异常由全局拦截器提示
      }
    },
    async confirmConversion () {
      if (!this.canConfirm) return

      this.confirming = true
      try {
        const res = await this.$q.newProduct.sliceConvertConfirm({
          data: {
            taskId: this.taskId
          }
        })
        if (res.code == 200) {
          this.$message.success('确认上传成功')
          this.$emit('success')
          this.closeDialog()
        }
      } catch (error) {
        // 请求异常由全局拦截器提示
      } finally {
        this.confirming = false
      }
    },
    async cancelConversion () {
      if (this.cancelling) return
      if (!this.taskId) {
        if (this.loading) {
          this.cancelAfterScan = true
        }
        this.closeDialog()
        return
      }

      this.cancelling = true
      try {
        const res = await this.$q.newProduct.sliceConvertCancel({
          data: {
            taskId: this.taskId
          }
        })
        if (res.code == 200) {
          this.$message.success('文件层级转换已取消')
          this.closeDialog()
        }
      } catch (error) {
        // 请求异常由全局拦截器提示
      } finally {
        this.cancelling = false
      }
    },
    handleDialogClose () {
      if (this.skipCancel) {
        this.skipCancel = false
        return
      }
      this.cancelConversion()
    },
    closeDialog () {
      this.skipCancel = true
      this.$emit('update:visible', false)
      this.$emit('close')
    }
  }
}
</script>

<style lang="stylus" scoped>
.conversion-dialog
  color #25313c
  font-size 12px

.conversion-summary
  display flex
  align-items center
  justify-content space-between
  height 28px
  font-size 13px

  strong
    font-size 15px

  span
    color #65727d

  b
    color #ff4d5b
    font-weight 600

.conversion-note
  margin 0 0 12px
  padding 9px 12px
  border-left 3px solid #0db49c
  background #f1faf8
  color #687680

  i
    margin-right 7px
    color #0ab39c

.conversion-workspace
  display grid
  grid-template-columns minmax(270px, 1fr) minmax(310px, 1.15fr) minmax(340px, 1.25fr)
  height 474px
  border 1px solid #d8e0e5
  background #fff

.directory-panel,
.mapping-panel
  min-width 0
  overflow hidden

.directory-panel + .mapping-panel,
.mapping-panel + .directory-panel
  border-left 1px solid #d8e0e5

.panel-heading
  display flex
  align-items center
  justify-content space-between
  height 40px
  padding 0 13px
  border-bottom 1px solid #d8e0e5
  background #f7f9fa
  color #34434e

  strong
    font-size 13px

  > span
    color #87939d

.new-directory-actions
  display flex
  align-items center
  gap 10px
  white-space nowrap

.directory-list
  height calc(100% - 40px)
  overflow-y auto
  padding 5px

.directory-item
  box-sizing border-box
  width 100%
  min-height 58px
  display flex
  align-items flex-start
  gap 8px
  padding 9px 8px
  border 1px solid transparent
  background #fff
  color #41515e
  text-align left

.old-directory-item
  cursor pointer

  &:hover
    background #f4fbfa

  &.selected
    border-color #ff9e9e
    background #fff5f4

  &.out-of-rule
    background #fff8f7

.target-directory-item
  position relative
  cursor default

  &.active
    background #f0fbf8

.directory-icon
  flex 0 0 16px
  margin-top 2px
  color #f0a12f
  font-size 16px

.el-icon-document
  color #8395a2

.directory-item-main
  min-width 0
  flex 1
  line-height 18px

  strong,
  small
    display block
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

  strong
    color #40515d
    font-size 13px
    font-weight 500

  small
    color #8b98a1
    font-size 11px

.directory-item em
  flex 0 0 auto
  margin-top 5px
  color #00ae91
  font-size 11px
  font-style normal

.directory-item em.is-pending
  color #ff5662

.directory-item em.is-ignored
  color #8d99a2

.directory-empty
  padding-top 75px
  color #9aa5ac
  text-align center

.mapping-panel
  background linear-gradient(to bottom, #fff, #fcfdfd)

.mapping-content
  padding 42px 24px 20px

.source-card
  display flex
  align-items flex-start
  min-height 86px
  padding 16px 14px
  border 1px solid #d9e3e7
  border-radius 4px
  box-sizing border-box

.source-info
  min-width 0
  margin-left 9px

  small,
  strong,
  em
    display block

  small
    margin-bottom 5px
    color #8a98a2

  strong
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    color #283c48
    font-size 13px

  em
    margin-top 3px
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    color #8a98a2
    font-size 11px
    font-style normal

.detail-link
  padding 0
  border 0
  background transparent
  color #08a98f
  cursor pointer
  font-size 12px

  &:hover
    color #078671

  &:disabled
    color #aeb9bf
    cursor not-allowed

.source-info > .detail-link
  display block
  margin-top 7px

.rename-actions
  margin-top 2px

  .el-button
    padding 3px 8px 0 0

.mapping-arrow
  display flex
  align-items center
  justify-content center
  height 56px

  i
    display flex
    align-items center
    justify-content center
    width 28px
    height 28px
    border-radius 50%
    background #0bb39d
    color #fff
    font-size 18px

.target-select
  display block

  > span
    display block
    margin-bottom 7px
    color #344852
    font-weight 600

  >>> .el-select
    width 100%

.mapping-help
  margin 7px 0 0
  color #00ad91

  &.is-pending
    color #ff5b63

  &.is-ignored
    color #87949c

.empty-state
  padding-top 180px
  color #9aa5ac
  text-align center

.conversion-footer
  display flex
  align-items center
  justify-content space-between
  min-height 50px
  color #ff5b62

  > div
    white-space nowrap

  .el-button
    min-width 58px
    margin-left 10px

  .el-button--primary:disabled
    border-color #d8e1e4
    background #d8e1e4
    color #fff

.preview-dialog >>> .el-dialog__body
  padding 0 20px 20px

.preview-toolbar
  display flex
  align-items center
  justify-content space-between
  min-height 38px
  border-bottom 1px solid #ebeef5
  color #7d8b94

  .el-button
    margin-left 0

.preview-list
  min-height 180px
  max-height 360px
  overflow-y auto
  padding-top 8px

.preview-entry
  display flex
  align-items center
  width 100%
  min-height 38px
  padding 0 8px
  border 0
  background #fff
  color #41515e
  cursor pointer
  text-align left

  &:hover
    background #f4fbfa

  > i
    width 22px
    color #f0a12f
    font-size 16px

  span
    flex 1

  small
    color #97a2a9

.preview-file-entry
  cursor default

  &:hover
    background #fff

.preview-empty
  padding-top 75px
  color #9aa5ac
  text-align center

@media screen and (max-width: 900px)
  .conversion-workspace
    grid-template-columns 1fr
    height auto

  .directory-panel,
  .mapping-panel
    min-height 260px

  .directory-panel + .mapping-panel,
  .mapping-panel + .directory-panel
    border-top 1px solid #d8e0e5
    border-left 0

  .conversion-footer
    align-items flex-start
    flex-direction column
    gap 10px
    padding-top 12px
</style>
